import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { debounceTime, filter, from, fromEvent, map, Observable, startWith, switchMap } from 'rxjs';
import { PageNavigationItem } from '../data-services/navigation/navigation.interface';
import { DimensionService } from 'app/analyze/service/dimension-service/dimension.service';
import { NavigationCategory, NavigationService } from '../data-services/navigation/navigation.service';
import { NavigationDataService } from '../data-services/navigation/navigation-data.service';

@Injectable({
  providedIn: 'root',
})
export class PageNavigationService {
  private router = inject(Router);
  private dimensionService = inject(DimensionService);
  private navigationService = inject(NavigationService);
  private navigationDataService = inject(NavigationDataService);

  private paramId$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    startWith(null),
    map(() => this.getParamFromTree(this.router.routerState.root)),
    filter((id): id is string => !!id),
  );

  private selectedPortfolioId = toSignal(
    this.navigationService
      .getNavigationObservable([NavigationCategory.PortfolioLevelSelectedPortfolio])
      .pipe(map(portfolio => portfolio.selectedPortfolioId)),
  );

  private portfolios = toSignal(
    this.navigationDataService.getSubPortfolioListData().pipe(map(data => data.portfolios)),
  );

  private static readonly MOBILE_BREAKPOINT = 1024;

  public isMobile = toSignal(
    fromEvent(window, 'resize').pipe(
      debounceTime(100),
      map(() => window.innerWidth < PageNavigationService.MOBILE_BREAKPOINT),
      startWith(window.innerWidth < PageNavigationService.MOBILE_BREAKPOINT),
    ),
  );

  public currentSegment = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects.split('/').at(-1)),
      startWith(this.router.url.split('/').at(-1)),
    ),
  );

  public portfolioTitle = computed(
    () => this.portfolios().find(portfolio => portfolio.id === this.selectedPortfolioId())?.name || '',
  );

  public parkTitle = this.createParamSignal(id =>
    from(this.dimensionService.getParkByName(id)).pipe(map(park => park?.frontEndCode)),
  );

  public turbineTitle = this.createParamSignal(id =>
    from(this.dimensionService.getTurbineByName(parseInt(id, 10))).pipe(
      map(t => (t ? `${t.name.replace(/_/g, ' ')} (${t.serialDisplay})` : '')),
    ),
  );

  public getMappedViews(baseViews: PageNavigationItem[]) {
    return computed(() => {
      const activeId = this.currentSegment();
      return baseViews.map(view => ({
        ...view,
        active: view.id === activeId,
      }));
    });
  }

  private getParamFromTree(route: any): string | null {
    let id = route.snapshot.params.id;
    while (route.firstChild) {
      route = route.firstChild;
      if (route.snapshot?.params.id) {
        id = route.snapshot.params.id;
      }
    }
    return id;
  }

  private createParamSignal<T>(transform: (id: string) => Observable<T>) {
    return toSignal(this.paramId$.pipe(switchMap(transform)));
  }
}
